import { Component, EventBus } from '../core/smpl-components.js'
import { Events } from '../core/events.js'
import { showAddition } from '../modules/addition-animation.js'

// -----------------------------------------------------------------------------
// Add to cart — one-click add from a product card, without leaving the page.
//
// Posts to WooCommerce's own `add_to_cart` AJAX handler (WC_AJAX), so all of
// Woo's validation, stock checks, session handling and add-to-cart hooks stay
// intact. On success it announces the refreshed fragments on the bus; the cart
// component (components/cart.js) is what actually paints the new count.
//
// The element is a real anchor pointing at `?add-to-cart=ID`, so every failure
// path is the same: let the browser follow the link and have WooCommerce do the
// add server-side, notices and all. Selector: `.js-add-to-cart`.
// -----------------------------------------------------------------------------
export default class extends Component {
	prepare() {
		// Set on <body> in base.twig: it sits outside the Swup container, so it
		// survives navigation. Absent when WooCommerce is inactive.
		this.endpoint = document.body.dataset.wcAddToCartUrl
		this.productId = this.element.dataset.productId
		this.quantity = this.element.dataset.quantity || '1'

		this.isBusy = false
		this.bndClick = this.onClick.bind(this)
	}

	mount() {
		// Nothing to hook into: leave the plain link doing its job.
		if (!this.endpoint || !this.productId) return

		this.element.addEventListener('click', this.bndClick)
	}

	unmount() {
		this.element.removeEventListener('click', this.bndClick)
		this.controller?.abort()
	}

	async onClick(ev) {
		ev.preventDefault()
		if (this.isBusy) return

		// Straight away on the tap, not after the request: it should feel instant.
		// After the busy check, so a double tap gives one "+1" (and one add). On
		// the rare failure the redirect below takes over anyway.
		showAddition(this.element)

		this.setBusy(true)
		this.controller = new AbortController()

		try {
			const response = await fetch(this.endpoint, {
				method: 'POST',
				credentials: 'same-origin',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body: new URLSearchParams({ product_id: this.productId, quantity: this.quantity }).toString(),
				signal: this.controller.signal,
			})

			if (!response.ok) throw new Error(`add to cart failed (${response.status})`)

			const data = await response.json()

			// WooCommerce answers validation failures with HTTP 200 and an error
			// flag, so `response.ok` is not enough. Follow it through to the
			// product page, where Woo renders the actual notice.
			if (!data || data.error) {
				window.location.href = data?.product_url || this.element.href
				return
			}

			EventBus.emit(Events.CART_UPDATED, { fragments: data.fragments, cartHash: data.cart_hash })
			this.setBusy(false)
		} catch (error) {
			if (error.name === 'AbortError') return

			// Network or parse failure: fall back to the non-JS path rather than
			// silently swallowing the click.
			console.warn('[add-to-cart]', error)
			window.location.href = this.element.href
		}
	}

	setBusy(isBusy) {
		this.isBusy = isBusy
		this.element.classList.toggle('is-loading', isBusy)
		this.element.setAttribute('aria-busy', isBusy ? 'true' : 'false')
	}
}
